import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Sparkles, Captions, Type, Contrast, Accessibility } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import BottomNav from "../components/BottomNav";
import PrivacySection from "../components/PrivacySection";
import ReportInbox from "../components/ReportInbox";

const PROVIDERS = [
  { key: "openai", model: "gpt-5.4", label: "ChatGPT", sub: "OpenAI · gpt-5.4", emoji: "💬" },
  { key: "gemini", model: "gemini-3-flash-preview", label: "Gemini", sub: "Google · gemini-3 flash", emoji: "✨" },
  { key: "anthropic", model: "claude-sonnet-4-6", label: "Claude", sub: "Anthropic · claude-sonnet-4-6", emoji: "🧠" },
];

export default function Settings() {
  const { user, updateSettings } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(user?.ai_provider || "openai");
  const [saving, setSaving] = useState(false);
  const [captions, setCaptions] = useState(localStorage.getItem("sorted_captions") !== "off");
  const [largeText, setLargeText] = useState(localStorage.getItem("sorted_largetext") === "on");

  const pick = async (p) => {
    setProvider(p.key); setSaving(true);
    try { await updateSettings(p.key, p.model); toast.success(`Millie will now think with ${p.label}`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = (key, val, setter) => {
    setter(val); localStorage.setItem(key, val ? "on" : "off");
    toast.success("Preference saved");
  };

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#EFE6F5] pb-28">
        <div className="px-6 pt-12 flex items-center justify-between">
          <button onClick={() => navigate("/millie")} data-testid="settings-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
          <h1 className="font-hand text-4xl text-sorted-ink">Settings</h1>
          <span className="w-11" />
        </div>

        <div className="px-6 mt-6">
          <h2 className="font-heading text-lg font-semibold text-sorted-ink flex items-center gap-2 mb-3"><Sparkles size={18} strokeWidth={2.5} className="text-sorted-rose" /> Millie's AI brain</h2>
          <div className="space-y-2.5">
            {PROVIDERS.map((p) => (
              <button key={p.key} onClick={() => pick(p)} disabled={saving} data-testid={`ai-${p.key}`}
                className={`w-full bg-white rounded-3xl border-2 p-4 flex items-center gap-3 text-left shadow-soft transition-colors ${provider === p.key ? "border-sorted-rose" : "border-[#FDECEF]"}`}>
                <span className="text-2xl">{p.emoji}</span>
                <span className="flex-1">
                  <span className="font-heading font-semibold text-sorted-ink block">{p.label}</span>
                  <span className="text-xs font-semibold text-sorted-slate">{p.sub}</span>
                </span>
                {provider === p.key && <Check size={20} strokeWidth={3} className="text-sorted-rose" />}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 mt-8">
          <h2 className="font-heading text-lg font-semibold text-sorted-ink flex items-center gap-2 mb-3"><Accessibility size={18} strokeWidth={2.5} className="text-purple-500" /> Accessibility</h2>
          <div className="bg-white rounded-3xl border-2 border-[#FDECEF] shadow-soft divide-y divide-[#FDECEF]">
            <ToggleRow icon={Captions} label="Millie's captions" desc="Always show what Millie says" on={captions} onClick={() => toggle("sorted_captions", !captions, setCaptions)} testId="toggle-captions" />
            <ToggleRow icon={Type} label="Larger text" desc="Increase text size" on={largeText} onClick={() => toggle("sorted_largetext", !largeText, setLargeText)} testId="toggle-largetext" />
            <ToggleRow icon={Contrast} label="High contrast" desc="Coming soon" on={false} disabled onClick={() => toast("High contrast theme is coming soon")} testId="toggle-contrast" />
          </div>
        </div>

        <PrivacySection />
        {user?.role === "admin" && <ReportInbox />}

        <div className="px-6 mt-8">
          <p className="text-center text-xs text-sorted-slate font-semibold">Signed in as {user?.email}</p>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}

function ToggleRow({ icon: Icon, label, desc, on, onClick, disabled, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} disabled={disabled} className="w-full flex items-center gap-3 p-4 text-left disabled:opacity-60">
      <Icon size={18} strokeWidth={2.5} className="text-sorted-slate shrink-0" />
      <span className="flex-1">
        <span className="font-heading font-semibold text-sorted-ink block leading-tight">{label}</span>
        <span className="text-xs text-sorted-slate">{desc}</span>
      </span>
      <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${on ? "bg-sorted-rose justify-end" : "bg-muted justify-start"}`}>
        <span className="w-5 h-5 rounded-full bg-white shadow" />
      </span>
    </button>
  );
}
